import React, { useState } from 'react';
import { Container, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Button, Snackbar, Alert, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, IconButton } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getLicenses, createLicense, revokeLicense, activateLicense, updateLicense, deleteLicense } from '../api/licenses';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import { useNavigate } from 'react-router-dom';
import { License } from '../types/license';
import { LicenseDetailDialog } from '../components/LicenseDetailDialog';

const Licenses: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = useSelector((state: RootState) => state.auth.user);
  const isAdmin = user?.role?.toUpperCase() === 'ADMIN';
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [errorDetails, setErrorDetails] = useState<any | null>(null);
  const [selectedLicense, setSelectedLicense] = useState<License | null>(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [licenseToDelete, setLicenseToDelete] = useState<License | null>(null);

  const { data: licenses, isLoading } = useQuery({
    queryKey: ['licenses'],
    queryFn: getLicenses
  });

  const createLicenseMutation = useMutation({
    mutationFn: () => createLicense({ expiresAt: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString() }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['licenses'] });
      setSuccessMessage('ライセンスが作成されました');
    }
  });

  const revokeLicenseMutation = useMutation({
    mutationFn: revokeLicense,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['licenses'] });
      setSuccessMessage('ライセンスが無効化されました');
    },
    onError: (error: any) => {
      setErrorMessage('ライセンスの無効化に失敗しました');
      console.error('Failed to revoke license:', error);
    }
  });

  const activateLicenseMutation = useMutation({
    mutationFn: activateLicense,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['licenses'] });
      setSuccessMessage('ライセンスが有効化されました');
    },
    onError: (error: any) => {
      const errorData = error?.response?.data;
      if (errorData?.quota_info) {
        // ライセンス枠不足エラーの場合
        setErrorMessage(errorData.error || 'ライセンス枠が不足しています');
        setErrorDetails(errorData.quota_info);
        setErrorDialogOpen(true);
      } else {
        setErrorMessage(errorData?.error || 'ライセンスの有効化に失敗しました');
      }
      console.error('Failed to activate license:', error);
    }
  });

  const updateLicenseMutation = useMutation({
    mutationFn: ({ licenseId, updates }: { licenseId: string; updates: { expiresAt?: string } }) =>
      updateLicense(licenseId, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['licenses'] });
      setSuccessMessage('ライセンスが更新されました');
    },
  });

  const deleteLicenseMutation = useMutation({
    mutationFn: deleteLicense,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['licenses'] });
      setSuccessMessage('ライセンスが削除されました');
      setDeleteDialogOpen(false);
      setLicenseToDelete(null);
    },
    onError: (error: any) => {
      const errorData = error?.response?.data;
      setErrorMessage(errorData?.error || 'ライセンスの削除に失敗しました');
      console.error('Failed to delete license:', error);
    }
  });

  const handleCreateLicense = () => {
    createLicenseMutation.mutate();
  };

  const handleRevokeLicense = (id: string) => {
    revokeLicenseMutation.mutate(id);
  };

  const handleActivateLicense = (id: string) => {
    activateLicenseMutation.mutate(id);
  };

  const handleUpdateLicense = (licenseId: string, updates: { expiresAt?: string }) => {
    updateLicenseMutation.mutate({ licenseId, updates });
  };

  const handleDeleteClick = (license: License) => {
    setLicenseToDelete(license);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = () => {
    if (licenseToDelete) {
      deleteLicenseMutation.mutate(licenseToDelete.id);
    }
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setLicenseToDelete(null);
  };

  const handleCloseSnackbar = () => {
    setSuccessMessage(null);
    setErrorMessage(null);
  };

  const handleCloseErrorDialog = () => {
    setErrorDialogOpen(false);
    setErrorDetails(null);
  };

  const handleShowDetails = (license: License) => {
    setSelectedLicense(license);
    setDetailDialogOpen(true);
  };

  const handleCloseDetailDialog = () => {
    setDetailDialogOpen(false);
    setSelectedLicense(null);
  };

  if (isLoading) {
    return <Typography>読み込み中...</Typography>;
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom>
        ライセンス管理
      </Typography>
      {isAdmin && (
        <Button
          variant="contained"
          color="primary"
          sx={{ mb: 2 }}
          data-testid="create-license-button"
          onClick={() => {
            console.log('ライセンス作成ボタンがクリックされました');
            navigate('/licenses/create');
          }}
        >
          新規ライセンス発行
        </Button>
      )}
      <TableContainer component={Paper} data-testid="licenses-table">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>ライセンスキー</TableCell>
              <TableCell>アプリケーション</TableCell>
              <TableCell>ユーザー名</TableCell>
              <TableCell>ステータス</TableCell>
              <TableCell>アクティベーション</TableCell>
              <TableCell>発行日</TableCell>
              <TableCell>有効期限</TableCell>
              <TableCell>アクション</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {licenses?.map((license) => (
              <TableRow key={license.id} data-testid="license-row">
                <TableCell>{license.key}</TableCell>
                <TableCell>{license.applicationName || license.applicationId || '-'}</TableCell>
                <TableCell>{license.userName || '-'}</TableCell>
                <TableCell>{license.status}</TableCell>
                <TableCell>
                  {license.currentActivations || 0} / {license.maxActivations || 1}
                </TableCell>
                <TableCell>{new Date(license.issuedAt).toLocaleDateString('ja-JP')}</TableCell>
                <TableCell>
                  {license.expiresAt
                    ? new Date(license.expiresAt).toLocaleDateString('ja-JP')
                    : '無期限'}
                </TableCell>
                <TableCell>
                  <Button
                    size="small"
                    color="primary"
                    onClick={() => handleShowDetails(license)}
                  >
                    詳細
                  </Button>
                  {isAdmin && (
                    <>
                      {license.status === 'REVOKED' ? (
                        <Button
                          size="small"
                          color="success"
                          data-testid="activate-button"
                          onClick={() => handleActivateLicense(license.id)}
                        >
                          有効化
                        </Button>
                      ) : (
                        <Button
                          size="small"
                          color="error"
                          data-testid="deactivate-button"
                          onClick={() => handleRevokeLicense(license.id)}
                        >
                          無効化
                        </Button>
                      )}
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDeleteClick(license)}
                        title="削除"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Snackbar
        open={!!successMessage}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%' }}>
          {successMessage}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!errorMessage && !errorDialogOpen}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity="error" sx={{ width: '100%' }}>
          {errorMessage}
        </Alert>
      </Snackbar>

      {/* ライセンス枠不足エラーダイアログ */}
      <Dialog
        open={errorDialogOpen}
        onClose={handleCloseErrorDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>ライセンス枠不足</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {errorMessage}
          </DialogContentText>
          {errorDetails && (
            <>
              <Typography variant="subtitle2" gutterBottom>
                ライセンス枠の詳細:
              </Typography>
              <Typography variant="body2">
                • 購入済み枠: {errorDetails.license_quota_total || 0}
              </Typography>
              <Typography variant="body2">
                • 使用中枠: {errorDetails.license_quota_used || 0}
              </Typography>
              <Typography variant="body2">
                • 利用可能枠: {errorDetails.license_quota_available || 0}
              </Typography>
              {errorDetails.is_quota_expired && (
                <Typography variant="body2" color="warning.main" sx={{ mt: 1 }}>
                  ⚠️ ライセンス枠情報が古い可能性があります。最新の情報はサブスクライトにてご確認ください。
                </Typography>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseErrorDialog} color="primary">
            閉じる
          </Button>
        </DialogActions>
      </Dialog>

      <LicenseDetailDialog
        open={detailDialogOpen}
        license={selectedLicense}
        onClose={handleCloseDetailDialog}
        onUpdate={handleUpdateLicense}
      />

      {/* 削除確認ダイアログ */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCancelDelete}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>ライセンスの削除確認</DialogTitle>
        <DialogContent>
          <DialogContentText>
            本当にこのライセンスを削除しますか？
            {licenseToDelete && (
              <>
                <br />
                <br />
                <strong>ライセンスキー:</strong> {licenseToDelete.key}
                <br />
                <strong>ユーザー:</strong> {licenseToDelete.userName || '-'}
                <br />
                <strong>ステータス:</strong> {licenseToDelete.status}
                <br />
                <br />
                この操作は取り消すことができません。関連するアクティベーション記録も削除されます。
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete} color="primary">
            キャンセル
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            disabled={deleteLicenseMutation.isPending}
          >
            削除する
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Licenses; 